// subscriptionUtils.ts
// Utilities for managing subscription cancellation flows
// Handles deep links to iOS Settings and Google Play Store

import { Platform, Linking, Alert } from 'react-native';

export type SubscriptionSource = 'app_store' | 'play_store' | 'stripe' | 'promotional' | 'unknown';

export interface SubscriptionSourceInfo {
  displayName: string;
  canOpenSettings: boolean;
  instructions: string;
  buttonLabel: string;
}

/**
 * Get display information for a subscription source
 */
export function getSubscriptionSourceInfo(source: string | undefined): SubscriptionSourceInfo {
  switch (source) {
    case 'app_store':
      return {
        displayName: 'App Store',
        canOpenSettings: Platform.OS === 'ios',
        instructions: 'To cancel your subscription, go to Settings > Apple ID > Subscriptions',
        buttonLabel: 'Open iOS Settings',
      };
    
    case 'play_store':
      return {
        displayName: 'Google Play',
        canOpenSettings: Platform.OS === 'android',
        instructions: 'To cancel your subscription, open Google Play Store > Account > Subscriptions',
        buttonLabel: 'Open Google Play',
      };
    
    case 'stripe':
      return {
        displayName: 'Stripe',
        canOpenSettings: false,
        instructions: 'Your subscription is managed externally. Please contact support to cancel.',
        buttonLabel: 'Contact Support',
      };
    
    case 'promotional':
      return {
        displayName: 'Promotional',
        canOpenSettings: false,
        instructions: 'You have a promotional subscription. Contact support if you have questions.',
        buttonLabel: 'Contact Support',
      };
    
    case 'unknown':
    default:
      return {
        displayName: 'Unknown',
        canOpenSettings: false,
        instructions: 'To manage your subscription, please contact support.',
        buttonLabel: 'Contact Support',
      };
  }
}

/**
 * Open iOS subscription settings
 * Uses app-settings: URL scheme which opens the Settings app
 */
export async function openIOSSubscriptionSettings(): Promise<{ success: boolean; error?: string }> {
  try {
    if (Platform.OS !== 'ios') {
      return { success: false, error: 'Not available on this platform' };
    }

    // iOS 13.4+ supports direct link to subscriptions
    // For older versions, we open general settings
    const url = 'app-settings:';
    
    const canOpen = await Linking.canOpenURL(url);
    if (!canOpen) {
      return { 
        success: false, 
        error: 'Cannot open settings. Please go to Settings > Apple ID > Subscriptions manually.' 
      };
    }

    await Linking.openURL(url);
    return { success: true };
  } catch (error) {
    console.error('[subscriptionUtils] Error opening iOS settings:', error);
    return { 
      success: false, 
      error: 'Failed to open settings. Please go to Settings > Apple ID > Subscriptions manually.' 
    };
  }
}

/**
 * Open Android Play Store subscription management
 */
export async function openAndroidSubscriptionSettings(): Promise<{ success: boolean; error?: string }> {
  try {
    if (Platform.OS !== 'android') {
      return { success: false, error: 'Not available on this platform' };
    }

    // Link to Play Store subscriptions page
    const url = 'https://play.google.com/store/account/subscriptions';
    
    const canOpen = await Linking.canOpenURL(url);
    if (!canOpen) {
      return { 
        success: false, 
        error: 'Cannot open Play Store. Please open it manually and go to Account > Subscriptions.' 
      };
    }

    await Linking.openURL(url);
    return { success: true };
  } catch (error) {
    console.error('[subscriptionUtils] Error opening Play Store:', error);
    return { 
      success: false, 
      error: 'Failed to open Play Store. Please open it manually and go to Account > Subscriptions.' 
    };
  }
}

/**
 * Open subscription management based on source
 * Returns true if opened successfully, false if user should contact support
 */
export async function openSubscriptionManagement(
  source: string | undefined,
  managementURL?: string | null
): Promise<{ success: boolean; shouldContactSupport: boolean; error?: string }> {
  // Try RevenueCat management URL first if available
  if (managementURL) {
    try {
      const canOpen = await Linking.canOpenURL(managementURL);
      if (canOpen) {
        await Linking.openURL(managementURL);
        return { success: true, shouldContactSupport: false };
      }
    } catch (error) {
      console.warn('[subscriptionUtils] Failed to open management URL:', error);
      // Fall through to platform-specific handling
    }
  }

  // Platform-specific handling
  switch (source) {
    case 'app_store': {
      const result = await openIOSSubscriptionSettings();
      return { 
        success: result.success, 
        shouldContactSupport: !result.success,
        error: result.error 
      };
    }
    
    case 'play_store': {
      const result = await openAndroidSubscriptionSettings();
      return { 
        success: result.success, 
        shouldContactSupport: !result.success,
        error: result.error 
      };
    }
    
    case 'stripe':
    case 'promotional':
    case 'unknown':
    default:
      // These require contacting support
      return { 
        success: false, 
        shouldContactSupport: true,
        error: 'Please contact support to manage your subscription.'
      };
  }
}

/**
 * Show fallback instructions in an alert if deep link fails
 */
export function showFallbackInstructions(source: string | undefined): void {
  const info = getSubscriptionSourceInfo(source);
  
  Alert.alert(
    'Manage Subscription',
    info.instructions,
    [{ text: 'OK' }]
  );
}

